import { mapTaskKey } from "../canonical/map-keys";

import type { Room } from "../schema";

// Documents page model (2026-09-06, Bill; trimmed the same day; custom
// folders 2026-09-07).
//
// The Documents page is a Dropbox-style list over the room's uploads: a drop
// area, then folders. Everything here is derived from the room JSON (the
// Onboarding Checklist's tasks and templates), the uploads list (files plus
// the per-file metadata the API stores alongside them) and the room state
// (custom folders). A file's folder lives on the file.
//
//   - Fixed folders (FIXED_FOLDERS) exist in every room. "Uploads" is where a
//     file lands unless it was dropped inside another folder. The supplier
//     can add folders of their own ("New folder"); those live in room state
//     as `documentFolders: { [id]: label }` and sit between the fixed ones and
//     the read-only Bison templates.
//   - A request (REQUEST_DEFS) binds a fixed folder to an Onboarding Checklist
//     task, so a file uploaded into Insurance is attached to the COI step and
//     the checklist row can say "<file> uploaded".

/** A folder id: one of the fixed ids below, or `f-<slug>` for a custom folder. */
export type FolderId = string;

export interface FolderDef {
    id: FolderId;
    label: string;
    /** Bison-provided templates; nothing can be uploaded, renamed or removed. */
    readOnly?: boolean;
    /** Made by the supplier from the page; stored in room state. */
    custom?: boolean;
}

export const FIXED_FOLDERS: readonly FolderDef[] = [
    { id: "uploads", label: "Uploads" },
    { id: "agreements", label: "Agreements" },
    { id: "insurance", label: "Insurance" },
    { id: "loa", label: "Letters of authorization" },
    { id: "credit", label: "Credit application" },
    { id: "payments", label: "Payments" },
    { id: "promotions", label: "Promotions" },
    { id: "product-spreadsheet", label: "Product Data" },
    { id: "product-images", label: "Product images" },
];

/** Upload folder that holds page media (never shown in the browser). */
export const ASSET_FOLDER = "room-assets";

export const BISON_FOLDER: FolderDef = { id: "bison", label: "Bison templates", readOnly: true };

export const DEFAULT_FOLDER: FolderId = "uploads";

const CUSTOM_PREFIX = "f-";

/** URL-safe form of a folder's label ("Product Data" -> "product-data"); what deep links carry. */
export function folderSlug(label: string): string {
    return label
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 36);
}

/** Folder id for a supplier-made folder, from its label. */
export function customFolderId(label: string): FolderId {
    return `${CUSTOM_PREFIX}${folderSlug(label) || "folder"}`;
}

// ─── Files ─────────────────────────────────────────────────────────────

export interface FileReview {
    status: "returned";
    note?: string;
    by?: string;
    at?: string;
}

/** One uploaded file, as returned by GET /api/rooms/{slug}/uploads. */
export interface DocumentFile {
    /** Stored name; the URL is built from it and never changes. */
    name: string;
    /** What the supplier sees. Rename changes this, not `name`. */
    displayName: string;
    size: number;
    contentType: string;
    uploadedAt: string;
    url: string;
    folder?: FolderId;
    /** Onboarding Checklist task key the file was uploaded against, when known. */
    taskKey?: string;
    /** For letters of authorization: which marketplace the letter covers. */
    marketplace?: string;
    uploadedBy?: string;
    uploadedByName?: string;
    review?: FileReview;
}

/** A Bison-provided template collected from the Onboarding Checklist tasks. */
export interface ProvidedTemplate {
    name: string;
    url: string;
    taskId?: string;
}

/** The key a deep link (`?file=`) names for a Bison template: its file name. Uploads use their stored `name`. */
export function templateKey(t: ProvidedTemplate): string {
    return t.url.split("/").pop() ?? t.url;
}

/** Which marketplace a letter of authorization covers, from its filename. */
export function guessMarketplace(filename: string, marketplaces: readonly string[]): string | null {
    const n = filename.toLowerCase().replace(/[^a-z0-9]/g, "");
    for (const m of marketplaces) {
        if (n.includes(m.toLowerCase().replace(/[^a-z0-9]/g, ""))) return m;
    }
    return null;
}

/** Short type label for the table's Type column ("PDF", "Image", "Spreadsheet"). */
export function fileTypeLabel(name: string, contentType?: string): string {
    const n = name.toLowerCase();
    const ext = n.includes(".") ? n.slice(n.lastIndexOf(".") + 1) : "";
    if (ext === "pdf" || contentType === "application/pdf") return "PDF";
    if (/^(png|jpe?g|gif|webp|tiff?|heic|heif|bmp|svg|psd|ai)$/.test(ext) || contentType?.startsWith("image/")) return "Image";
    if (/^(xlsx?|xlsm|csv|tsv|numbers|ods)$/.test(ext)) return "Spreadsheet";
    if (/^(docx?|pages|rtf|txt|md|odt)$/.test(ext)) return "Document";
    if (/^(pptx?|key|odp)$/.test(ext)) return "Presentation";
    if (/^(zip|rar|7z|tar|gz)$/.test(ext)) return "Archive";
    if (/^(mp4|mov|m4v|webm|avi)$/.test(ext) || contentType?.startsWith("video/")) return "Video";
    return ext ? ext.toUpperCase() : "File";
}

// ─── Folder ↔ checklist task binding ───────────────────────────────────

interface RequestDef {
    folder: FolderId;
    taskId: string;
}

const REQUEST_DEFS: readonly RequestDef[] = [
    { folder: "agreements", taskId: "seller-of-record-agreement-signed" },
    { folder: "agreements", taskId: "program-agreement-signed" },
    { folder: "credit", taskId: "credit-application" },
    { folder: "insurance", taskId: "certificate-of-insurance-issued" },
    { folder: "loa", taskId: "marketplace-agreements-granted" },
    { folder: "product-spreadsheet", taskId: "product-data-spreadsheet-started" },
    { folder: "product-spreadsheet", taskId: "final-product-data-provided" },
    { folder: "product-images", taskId: "final-product-data-provided" },
];

/** Folder an Onboarding Checklist upload lands in, by task id. */
export function folderForTask(taskId: string | undefined): FolderId | undefined {
    if (!taskId) return undefined;
    return REQUEST_DEFS.find((r) => r.taskId === taskId)?.folder;
}

// The checklist's signing steps show the room's own copy of the agreement
// (one named pill) from the Agreements page list, so a re-draft reaches
// the step with no data push (2026-10-14).
const AGREEMENT_FOR_TASK: Record<string, string> = {
    "seller-of-record-agreement-signed": "seller-of-record",
    "program-agreement-signed": "supplier-program-agreement",
};

export interface AgreementCopy {
    title: string;
    url: string;
}

/** The agreement a checklist step signs, from the room's Agreements list: the signed copy once pulled, else the draft. */
export function agreementForTask(room: Room, taskId: string | undefined): AgreementCopy | undefined {
    const id = taskId ? AGREEMENT_FOR_TASK[taskId] : undefined;
    if (!id) return undefined;
    for (const page of room.pages) {
        for (const w of page.widgets ?? []) {
            if (w.type !== "agreements") continue;
            const item = w.props.items.find((i) => i.id === id);
            const url = item?.signedUrl ?? item?.draftUrl;
            if (item && url) return { title: item.title, url };
        }
    }
    return undefined;
}

export interface FolderSummary extends FolderDef {
    count: number;
    files: DocumentFile[];
    templates: ProvidedTemplate[];
    /** State key of the checklist task uploads into this folder attach to. */
    taskKey?: string;
}

export interface DocumentsModel {
    /** Fixed folders, then custom ones, then Bison templates. */
    folders: FolderSummary[];
    folderById: Record<FolderId, FolderSummary>;
    templates: ProvidedTemplate[];
    /** Marketplaces that need a letter, from the checklist templates. */
    loaMarketplaces: string[];
}

export interface RoomStateLike {
    taskStatus?: Record<string, "pending" | "in_progress" | "complete">;
    /** Supplier-made folders, id → label. */
    documentFolders?: Record<string, string>;
}

interface MapTaskRef {
    taskId?: string;
    key: string;
    attachments: ProvidedTemplate[];
}

function collectMapTasks(room: Room): MapTaskRef[] {
    const out: MapTaskRef[] = [];
    for (const page of room.pages) {
        if (page.template !== "map") continue;
        for (const widget of page.widgets) {
            if (widget.type !== "map") continue;
            widget.props.phases.forEach((phase, phaseIdx) => {
                phase.tasks.forEach((task, taskIdx) => {
                    out.push({
                        taskId: task.taskId,
                        key: mapTaskKey({ widgetId: widget.id, task, phaseIdx, taskIdx }),
                        attachments: (task.attachments ?? []).map((a) => ({ ...a, taskId: task.taskId })),
                    });
                });
            });
        }
    }
    return out;
}

/** Marketplace names from "<Marketplace> LOA" template names. */
function loaMarketplacesFrom(templates: ProvidedTemplate[]): string[] {
    const names: string[] = [];
    for (const t of templates) {
        const m = t.name.match(/^(.+?)\s+LOA\b/i);
        if (m && !names.includes(m[1])) names.push(m[1]);
    }
    return names;
}

export function buildDocumentsModel(room: Room, files: DocumentFile[], state: RoomStateLike = {}): DocumentsModel {
    const tasks = collectMapTasks(room);
    const taskById = new Map(tasks.filter((t) => t.taskId).map((t) => [t.taskId as string, t]));

    // Every Bison template, deduped by URL, in checklist order.
    const templates: ProvidedTemplate[] = [];
    const seen = new Set<string>();
    for (const t of tasks) {
        for (const a of t.attachments) {
            if (seen.has(a.url)) continue;
            seen.add(a.url);
            templates.push(a);
        }
    }
    const loaTask = taskById.get("marketplace-agreements-granted");
    const loaMarketplaces = loaMarketplacesFrom(loaTask?.attachments ?? []);

    const custom: FolderDef[] = Object.entries(state.documentFolders ?? {})
        .filter(([id, label]) => id.startsWith(CUSTOM_PREFIX) && typeof label === "string" && label.trim())
        .map(([id, label]) => ({ id, label: label.trim(), custom: true }))
        .sort((a, b) => a.label.localeCompare(b.label));
    const defs: FolderDef[] = [...FIXED_FOLDERS, ...custom, BISON_FOLDER];
    const known = new Set(defs.map((d) => d.id));

    // A file whose folder is unknown (uploaded before folders existed, or a
    // custom folder that was removed) sits in Uploads.
    const byFolder = new Map<FolderId, DocumentFile[]>();
    for (const f of files) {
        // Room assets (the supplier's PDP stills on How We Partner) are page
        // media, not documents: never listed, never counted (2026-09-08).
        if (f.folder === ASSET_FOLDER) continue;
        const id = f.folder && known.has(f.folder) && f.folder !== BISON_FOLDER.id ? f.folder : DEFAULT_FOLDER;
        byFolder.set(id, [...(byFolder.get(id) ?? []), f]);
    }
    const newestFirst = (a: DocumentFile, b: DocumentFile) => b.uploadedAt.localeCompare(a.uploadedAt);
    for (const list of byFolder.values()) list.sort(newestFirst);

    const folders: FolderSummary[] = defs.map((def) => {
        const list = def.readOnly ? [] : (byFolder.get(def.id) ?? []);
        const taskId = REQUEST_DEFS.find((r) => r.folder === def.id)?.taskId;
        return {
            ...def,
            files: list,
            templates: def.readOnly ? templates : [],
            count: def.readOnly ? templates.length : list.length,
            taskKey: taskId ? taskById.get(taskId)?.key : undefined,
        };
    });
    const folderById = Object.fromEntries(folders.map((f) => [f.id, f])) as Record<FolderId, FolderSummary>;

    return { folders, folderById, templates, loaMarketplaces };
}

// ─── Small helpers ─────────────────────────────────────────────────────

export function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function formatShortDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) });
}
